import { fetchRequest } from '../../../services/http.service'
import { ClientLookupParams, DepositFilter, OpsApi, RedemptionFilter } from '../api/OpsApi'
import {
  Client,
  ClientSummary,
  CreateDistributorRequest,
  DistributorClient,
  UpdateDistributorRequest,
  ConfirmBatchRequest,
  ConfirmPaymentRequest,
  ConfirmRedemptionRequest,
  ConfirmSuccessFeeRequest,
  CreateBatchRequest,
  CreateRedemptionRequest,
  CreateSuccessFeeRequest,
  CreateValuationRequest,
  Deposit,
  Distributor,
  DocumentFile,
  DocumentMeta,
  FeeRate,
  Fund,
  FundConfig,
  GenerateApexRequest,
  GenerateImperiumRequest,
  GenerateResult,
  ManualDepositRequest,
  MgmtFeeRun,
  PaymentInfo,
  PositionImportResult,
  TrancheImportResult,
  FundSurplus,
  Redemption,
  RunMgmtFeeRequest,
  SetTotalUnitsRequest,
  StrategyKey,
  SuccessFeeRun,
  TemplateInfo,
  TotalUnitsChange,
  UnitBatch,
  StatementFormat,
  UpdateFeeRateRequest,
  UpdateFundConfigRequest,
  Valuation,
} from '../api/types'

/**
 * HTTP implementace kontraktu OpsApi nad axiosem projektu (fetchRequest).
 * baseURL je /api, proto se URL předávají bez prefixu (funds/... → /api/funds/...).
 *
 * BE posílá všechna číselná pole jednotek/cen/částek jako string (kvůli přesnosti),
 * FE typy je mají jako number — proto se odpovědi propouští přes `numify` a request
 * čísla se posílají jako string (BE je parsuje Fmt.dec).
 */

/**
 * Názvy klíčů, jejichž hodnotu BE posílá jako string a FE ji chce jako number.
 * `id`, `clientId`, časy, `year`, `clientCount` apod. zde ZÁMĚRNĚ nejsou.
 */
const NUMERIC_KEYS: { [key: string]: true } = {
  totalUnits: true,
  instrumentPrice: true,
  distributionRate: true,
  unitPrice: true,
  navTotal: true,
  amount: true,
  amountReceived: true,
  totalUnitsAt: true,
  totalUnitsBefore: true,
  totalUnitsAfter: true,
  units: true,
  priceOverride: true,
  unitsOverride: true,
  mgmtFeePa: true,
  successFeeRate: true,
  holdingValue: true,
  ratePa: true,
  fee: true,
  totalHoldingValue: true,
  totalFee: true,
  navBefore: true,
  navAfter: true,
  mfAmount: true,
  unitPriceAfter: true,
  yearEndPrice: true,
  totalFeeUnits: true,
  totalDistribution: true,
  rate: true,
  invested: true,
  value: true,
  profit: true,
  feeUnits: true,
  distribution: true,
  benchmarkPa: true,
  buyPrice: true,
  returnPa: true,
  baselineValue: true,
  commission: true,
  commissionRate: true,
  totalCommission: true,
  mfCommissionRate: true,
  sfCommissionRate: true,
  currentMfRate: true,
  currentSfRate: true,
  // Odkupy
  requestedUnits: true,
  requestedAmount: true,
  grossAmount: true,
  sfRate: true,
  sfFee: true,
  sfCommission: true,
  exitFeeRate: true,
  exitFee: true,
  netAmount: true,
  holdingUnits: true,
  sfUnits: true,
  commissionBasePa: true,
}

/**
 * Rekurzivně projde objekt/pole a u klíčů z NUMERIC_KEYS převede string → Number.
 * null/undefined nechá být, čísla nechá být, ostatní klíče rekurzivně prochází.
 */
/**
 * Distributor z BE: čísla přes numify, pásma ručně (klíče from/mf/sf do NUMERIC_KEYS nepatří,
 * „from“ používají i jiné odpovědi jako text). Objem jako číslo.
 */
function toDistributor(raw: any): Distributor {
  const d = numify(raw)
  return {
    ...d,
    volume: raw.volume != null ? Number(raw.volume) : null,
    commissionTiers: Array.isArray(raw.commissionTiers)
      ? raw.commissionTiers.map((t: any) => ({ from: Number(t.from), mf: Number(t.mf), sf: Number(t.sf) }))
      : null,
  } as Distributor
}

function numify(v: any): any {
  if (v === null || v === undefined) {
    return v
  }
  if (Array.isArray(v)) {
    return v.map(numify)
  }
  if (typeof v === 'object') {
    const out: any = {}
    for (const key in v) {
      if (Object.prototype.hasOwnProperty.call(v, key)) {
        const val = v[key]
        if (NUMERIC_KEYS[key]) {
          out[key] = typeof val === 'string' ? Number(val) : val
        } else {
          out[key] = numify(val)
        }
      }
    }
    return out
  }
  return v
}

/** number → string pro request; undefined/null nechá undefined. */
function optStr(x: number | undefined | null): string | undefined {
  return x === undefined || x === null ? undefined : String(x)
}

/**
 * BE core ClientDto → FE Client. `company` je Boolean, adresy jsou vnořené objekty,
 * telefon je `telephoneNumber`. Chybějící pole zůstávají undefined.
 */
function mapClient(dto: any): Client {
  const perm = dto.permanentAddress
  const cont = dto.contactAddress
  const company = !!dto.company
  const fullName = company
    ? dto.companyName || ''
    : ((dto.firstName || '') + ' ' + (dto.surname || '')).trim()
  return {
    id: String(dto.clientId),
    fullName,
    personalNumber: dto.personalNumber,
    companyId: dto.companyId,
    email: dto.email,
    createdAt: '',
    company,
    companyName: dto.companyName,
    firstName: dto.firstName,
    surname: dto.surname,
    titlePre: dto.titlePre,
    titleAfter: dto.titleAfter,
    birthDate: dto.birthDate,
    phone: dto.telephoneNumber,
    street: perm ? perm.street : undefined,
    town: perm ? perm.town : undefined,
    zipCode: perm ? perm.zipCode : undefined,
    contactStreet: cont ? cont.street : undefined,
    contactTown: cont ? cont.town : undefined,
    contactZip: cont ? cont.zipCode : undefined,
    identityCardNumber: dto.identityCardNumber,
  }
}

/** BE ClientDocumentDto → FE DocumentMeta. */
function mapDocument(dto: any): DocumentMeta {
  return {
    id: String(dto.clientDocumentId),
    clientId: String(dto.clientId),
    templateKey: dto.templateKey,
    label: dto.fileName || dto.templateKey,
    fileName: dto.fileName,
    strategy: dto.strategy,
    state: dto.state,
    createdAt: dto.createdAt,
  }
}

/** BE GenerateResultDto → FE GenerateResult. */
function mapGenerateResult(dto: any): GenerateResult {
  return {
    // Imperium s novým člověkem klienta nezakládá — přijde jen soubor ke stažení.
    client: dto.client ? mapClient(dto.client) : undefined,
    documents: (dto.documents || []).map(mapDocument),
    deposit: dto.deposit ? numify(dto.deposit) : undefined,
    credentialsSent: dto.credentialsSent ?? null,
    welcomeEmailSent: dto.welcomeEmailSent ?? null,
    clientEmailError: dto.clientEmailError ?? null,
    file: dto.file || undefined,
  }
}

/**
 * BE FundConfigDto → FE FundConfig. Přepínače chodí na top levelu vedle syrové mapy
 * `config` (v té jsou uložené, ale jsou tam i jiné klíče modulu) — čte se proto
 * nejdřív top level a teprve pak mapa.
 *
 * Default je ZAPNUTO: vypíná jen výslovné `false`. Chybějící klíč (starší BE, tenant
 * bez konfigurace) tak znamená dnešní chování, stejně jako to vyhodnocuje BE.
 */
function mapFundConfig(dto: any): FundConfig {
  const raw = (dto && dto.config) || {}
  const flag = (key: string): boolean =>
    dto && dto[key] !== undefined && dto[key] !== null ? dto[key] !== false : raw[key] !== false
  return {
    enabled: !!(dto && dto.enabled),
    emailsEnabled: flag('emailsEnabled'),
    distributorNotifications: flag('distributorNotifications'),
    createLogins: flag('createLogins'),
    createClients: flag('createClients'),
    correctionClientId: dto && dto.correctionClientId != null ? String(dto.correctionClientId) : null,
    successFeeClientId: dto && dto.successFeeClientId != null ? String(dto.successFeeClientId) : null,
  }
}

/**
 * BE řádek auditu ruční opravy jednotek → FE TotalUnitsChange. Hodnoty „před/po" už
 * převedl `numify` (klíče jsou v NUMERIC_KEYS). Kdo/kdy/„před/po" se v modulu u každé
 * entity jmenuje trochu jinak (createdBy vs. confirmedBy), proto se čtou tolerantně —
 * přesný název polí BE (fáze 7) doladí, mapper je jediné místo, kde se to opraví.
 */
function mapTotalUnitsChange(dto: any): TotalUnitsChange {
  const num = (value: any): number => (typeof value === 'number' ? value : Number(value))
  return {
    id: String(dto.id != null ? dto.id : ''),
    fundKey: dto.fundKey,
    // Názvy odpovídají TotalUnitsChangeDto na BE (ověřeno 4. 9.), tolerantní varianty pryč.
    totalUnitsBefore: num(dto.unitsBefore),
    totalUnitsAfter: num(dto.unitsAfter),
    note: dto.note || '',
    changedBy: dto.changedBy || '',
    changedAt: dto.changedAt || '',
  }
}

/**
 * BE souhrn načtení držby z core → FE PositionImportResult. `units`/`invested` chodí
 * jako string (jsou v NUMERIC_KEYS, takže je převedl už `numify`) — `num` je tu jen
 * pojistka pro případ, že by BE poslal řádek bez hodnoty. `rows`/`clients` jsou čísla.
 * `warnings` jsou hotové české věty z BE, do kterých se nesahá.
 */
function mapPositionImport(dto: any): PositionImportResult {
  const num = (value: any): number => (typeof value === 'number' ? value : Number(value || 0))
  const d = dto || {}
  return {
    preview: !!d.preview,
    importedAt: d.importedAt || null,
    importedBy: d.importedBy || null,
    rows: num(d.rows),
    clients: num(d.clients),
    funds: (d.funds || []).map((f: any) => ({
      fundKey: f.fundKey,
      feeClass: f.feeClass,
      clients: num(f.clients),
      units: num(f.units),
      invested: num(f.invested),
    })),
    warnings: d.warnings || [],
  }
}

/** BE SurplusDto → FE (čísla chodí jako string). */
function mapSurplus(d: any): FundSurplus {
  return {
    fundKey: d.fundKey,
    totalUnits: Number(d.totalUnits || 0),
    positionsUnits: Number(d.positionsUnits || 0),
    surplus: Number(d.surplus || 0),
    price: d.price != null ? Number(d.price) : null,
    correctionClientId: d.correctionClientId != null ? String(d.correctionClientId) : null,
  }
}

/** BE TrancheImportDto → FE. Čísla už převedl `numify`; počty jsou čísla z BE. */
function mapTrancheImport(dto: any): TrancheImportResult {
  const num = (value: any): number => (typeof value === 'number' ? value : Number(value || 0))
  const d = dto || {}
  return {
    preview: !!d.preview,
    importedAt: d.importedAt || null,
    importedBy: d.importedBy || null,
    tranches: num(d.tranches),
    alreadyKnown: num(d.alreadyKnown),
    clients: num(d.clients),
    funds: (d.funds || []).map((f: any) => ({
      fundKey: f.fundKey,
      feeClass: f.feeClass,
      tranches: num(f.tranches),
      units: num(f.units),
      amount: num(f.amount),
      firstDate: f.firstDate || null,
      lastDate: f.lastDate || null,
    })),
    settlements: (d.settlements || []).map((s: any) => ({
      fundKey: s.fundKey,
      year: num(s.year),
      yearEndPrice: num(s.yearEndPrice),
      feeUnits: num(s.feeUnits),
      fee: num(s.fee),
      clients: num(s.clients),
      exists: !!s.exists,
    })),
    warnings: d.warnings || [],
  }
}

export class OpsApiHttp implements OpsApi {
  // Konfigurace modulu
  getConfig(): Promise<FundConfig> {
    return fetchRequest.get<any>('funds/config').then((r) => mapFundConfig(r.data))
  }

  updateConfig(request: UpdateFundConfigRequest): Promise<FundConfig> {
    return fetchRequest.put<any>('funds/config', request).then((r) => mapFundConfig(r.data))
  }

  // Klienti
  searchClients(query: string): Promise<ClientSummary[]> {
    return fetchRequest
      .get<any>('funds/clients/search?query=' + query)
      .then((r) => r.data as ClientSummary[])
  }

  getClient(id: string): Promise<Client> {
    return fetchRequest.get<any>('funds/clients/' + id).then((r) => mapClient(r.data))
  }

  lookupClient(params: ClientLookupParams): Promise<ClientSummary[]> {
    const url =
      'funds/clients/lookup?personalNumber=' +
      (params.personalNumber || '') +
      '&companyId=' +
      (params.companyId || '')
    return fetchRequest.get<any>(url).then((r) => r.data as ClientSummary[])
  }

  // Dokumenty / generování
  listClientDocuments(clientId: string): Promise<DocumentMeta[]> {
    return fetchRequest
      .get<any>('funds/clients/' + clientId + '/documents')
      .then((r) => (r.data || []).map(mapDocument))
  }

  listImperiumTemplates(): Promise<TemplateInfo[]> {
    return fetchRequest.get<any>('funds/templates/imperium').then((r) => r.data as TemplateInfo[])
  }

  generateApexDocuments(request: GenerateApexRequest): Promise<GenerateResult> {
    const body = {
      clientId: request.clientId,
      client: request.client,
      strategy: request.strategy,
      variant: request.variant,
      amount: String(request.amount),
      accountNumber: request.accountNumber,
      entryFeePercent: String(request.entryFeePercent),
      entryFeeMode: request.entryFeeMode,
      sendCredentials: request.sendCredentials,
      sendWelcomeEmail: request.sendWelcomeEmail,
      createClient: request.createClient,
      distributorId: request.distributorId != null ? Number(request.distributorId) : undefined,
    }
    return fetchRequest
      .post<any>('funds/generate/apex', body)
      .then((r) => mapGenerateResult(r.data))
  }

  generateImperiumDocuments(request: GenerateImperiumRequest): Promise<GenerateResult> {
    return fetchRequest
      .post<any>('funds/generate/imperium', request)
      .then((r) => mapGenerateResult(r.data))
  }

  downloadDocument(documentId: string): Promise<DocumentFile> {
    return fetchRequest
      .get<any>('funds/documents/' + documentId + '/download')
      .then((r) => r.data as DocumentFile)
  }

  // Vklady / fronty
  listDeposits(filter?: DepositFilter): Promise<Deposit[]> {
    const parts: string[] = []
    if (filter && filter.status) {
      parts.push('status=' + filter.status)
    }
    if (filter && filter.search) {
      parts.push('search=' + filter.search)
    }
    const query = parts.length ? '?' + parts.join('&') : ''
    return fetchRequest.get<any>('funds/deposits' + query).then((r) => numify(r.data) as Deposit[])
  }

  confirmPayment(depositId: string, request: ConfirmPaymentRequest): Promise<Deposit> {
    const body = {
      amountReceived: String(request.amountReceived),
      sendEmail: request.sendEmail,
    }
    return fetchRequest
      .put<any>('funds/deposits/' + depositId + '/confirm-payment', body)
      .then((r) => numify(r.data) as Deposit)
  }

  deleteDeposit(depositId: string): Promise<void> {
    return fetchRequest.remove<any>('funds/deposits/' + depositId).then(() => undefined)
  }

  createManualDeposit(request: ManualDepositRequest): Promise<Deposit> {
    const body = {
      clientId: request.clientId,
      strategy: request.strategy,
      feeClass: request.feeClass,
      amount: String(request.amount),
      accountNumber: request.accountNumber,
      note: request.note,
    }
    return fetchRequest
      .post<any>('funds/deposits/manual', body)
      .then((r) => numify(r.data) as Deposit)
  }

  getPaymentInfo(depositId: string): Promise<PaymentInfo> {
    return fetchRequest
      .get<any>('funds/deposits/' + depositId + '/payment-info')
      .then((r) => numify(r.data) as PaymentInfo)
  }

  // Fondy / ocenění / přepočty
  listFunds(): Promise<Fund[]> {
    return fetchRequest.get<any>('funds/funds').then((r) => numify(r.data) as Fund[])
  }

  listValuations(fundKey: StrategyKey): Promise<Valuation[]> {
    return fetchRequest
      .get<any>('funds/funds/' + fundKey + '/valuations')
      .then((r) => numify(r.data) as Valuation[])
  }

  listBatches(fundKey?: StrategyKey): Promise<UnitBatch[]> {
    const query = fundKey ? '?fundKey=' + fundKey : ''
    return fetchRequest.get<any>('funds/batches' + query).then((r) => numify(r.data) as UnitBatch[])
  }

  getBatch(batchId: string): Promise<UnitBatch> {
    return fetchRequest.get<any>('funds/batches/' + batchId).then((r) => numify(r.data) as UnitBatch)
  }

  createValuation(request: CreateValuationRequest): Promise<Valuation> {
    const body = {
      fundKey: request.fundKey,
      date: request.date,
      items: request.items.map((it) => ({ label: it.label, amount: String(it.amount) })),
    }
    return fetchRequest.post<any>('funds/valuations', body).then((r) => numify(r.data) as Valuation)
  }

  updateInstrumentPrice(valuationId: string): Promise<Valuation> {
    return fetchRequest
      .post<any>(`funds/valuations/${valuationId}/instrument-price`, {})
      .then((r) => numify(r.data) as Valuation)
  }

  createBatch(request: CreateBatchRequest): Promise<UnitBatch> {
    const body = {
      fundKey: request.fundKey,
      valuationId: request.valuationId,
      assignmentDate: request.assignmentDate,
      participants: request.participants.map((p) => ({
        depositId: p.depositId,
        priceOverride: optStr(p.priceOverride),
        unitsOverride: optStr(p.unitsOverride),
      })),
    }
    return fetchRequest.post<any>('funds/batches', body).then((r) => numify(r.data) as UnitBatch)
  }

  setFundTotalUnits(fundKey: StrategyKey, request: SetTotalUnitsRequest): Promise<Fund> {
    // totalUnits je už přesný desítkový zápis (viz SetTotalUnitsRequest) — neprohánět přes Number.
    const body = { totalUnits: request.totalUnits, note: request.note }
    return fetchRequest
      .put<any>('funds/funds/' + fundKey + '/total-units', body)
      .then((r) => numify(r.data) as Fund)
  }

  listTotalUnitsHistory(fundKey: StrategyKey): Promise<TotalUnitsChange[]> {
    return fetchRequest
      .get<any>('funds/funds/' + fundKey + '/total-units/history')
      .then((r) => (numify(r.data) || []).map(mapTotalUnitsChange))
  }

  // Načtení držby klientů z core (počáteční stav modulu / oprava rozjetí)
  previewPositionImport(): Promise<PositionImportResult> {
    return fetchRequest
      .post<any>('funds/positions/import/preview', {})
      .then((r) => mapPositionImport(numify(r.data)))
  }

  importPositions(): Promise<PositionImportResult> {
    return fetchRequest
      .post<any>('funds/positions/import', {})
      .then((r) => mapPositionImport(numify(r.data)))
  }

  getSurplus(fundKey: StrategyKey): Promise<FundSurplus> {
    return fetchRequest.get<any>('funds/funds/' + fundKey + '/surplus').then((r) => mapSurplus(r.data))
  }

  assignSurplus(fundKey: StrategyKey, excelTotal?: number, sfUnits?: number, price?: number): Promise<FundSurplus> {
    const parts: string[] = []
    if (excelTotal != null) parts.push('excelTotal=' + String(excelTotal))
    if (sfUnits != null) parts.push('sfUnits=' + String(sfUnits))
    if (price != null) parts.push('price=' + String(price))
    const query = parts.length ? '?' + parts.join('&') : ''
    return fetchRequest
      .post<any>('funds/funds/' + fundKey + '/surplus/assign' + query, {})
      .then((r) => mapSurplus(r.data))
  }

  previewTrancheImport(): Promise<TrancheImportResult> {
    return fetchRequest
      .post<any>('funds/tranches/import/preview', {})
      .then((r) => mapTrancheImport(numify(r.data)))
  }

  importTranches(): Promise<TrancheImportResult> {
    return fetchRequest
      .post<any>('funds/tranches/import', {})
      .then((r) => mapTrancheImport(numify(r.data)))
  }

  // Kontrola + potvrzení přepočtu
  runBatchChecks(batchId: string): Promise<UnitBatch> {
    return fetchRequest
      .post<any>('funds/batches/' + batchId + '/checks', {})
      .then((r) => numify(r.data) as UnitBatch)
  }

  aiReviewBatch(batchId: string): Promise<UnitBatch> {
    return fetchRequest
      .post<any>('funds/batches/' + batchId + '/ai-review', {})
      .then((r) => numify(r.data) as UnitBatch)
  }

  confirmBatch(batchId: string, request: ConfirmBatchRequest): Promise<UnitBatch> {
    return fetchRequest
      .post<any>('funds/batches/' + batchId + '/confirm', request)
      .then((r) => numify(r.data) as UnitBatch)
  }

  rejectBatch(batchId: string): Promise<UnitBatch> {
    return fetchRequest
      .post<any>('funds/batches/' + batchId + '/reject', {})
      .then((r) => numify(r.data) as UnitBatch)
  }

  // Fee
  listDistributors(): Promise<Distributor[]> {
    return fetchRequest.get<any>('funds/distributors').then((r) => r.data as Distributor[])
  }

  // Sprava distributoru
  listDistributorsDetail(): Promise<Distributor[]> {
    return fetchRequest
      .get<any>('funds/distributors/detail')
      .then((r) => (r.data as any[]).map(toDistributor))
  }

  createDistributor(request: CreateDistributorRequest): Promise<Distributor> {
    const body = {
      ...request,
      mfCommissionRate: request.mfCommissionRate != null ? String(request.mfCommissionRate) : undefined,
      sfCommissionRate: request.sfCommissionRate != null ? String(request.sfCommissionRate) : undefined,
    }
    return fetchRequest.post<any>('funds/distributors', body).then((r) => numify(r.data) as Distributor)
  }

  createDistributorLogin(distributorId: string): Promise<Distributor> {
    return fetchRequest
      .post<any>(`funds/distributors/${distributorId}/login`, {})
      .then((r) => numify(r.data) as Distributor)
  }

  resetDistributorPassword(distributorId: string): Promise<Distributor> {
    return fetchRequest
      .post<any>(`funds/distributors/${distributorId}/login/reset-password`, {})
      .then((r) => numify(r.data) as Distributor)
  }

  updateDistributor(distributorId: string, request: UpdateDistributorRequest): Promise<Distributor> {
    const body = {
      ...request,
      // prazdny string = smazat sazbu (BE parseRate)
      mfCommissionRate: request.mfCommissionRate === undefined ? undefined
        : request.mfCommissionRate === null ? '' : String(request.mfCommissionRate),
      sfCommissionRate: request.sfCommissionRate === undefined ? undefined
        : request.sfCommissionRate === null ? '' : String(request.sfCommissionRate),
      commissionTiers: request.commissionTiers?.map((t) => ({
        from: String(t.from),
        mf: String(t.mf),
        sf: String(t.sf),
      })),
    }
    return fetchRequest.put<any>('funds/distributors/' + distributorId, body).then((r) => toDistributor(r.data))
  }

  listDistributorClients(distributorId: string): Promise<DistributorClient[]> {
    return fetchRequest
      .get<any>('funds/distributors/' + distributorId + '/clients')
      .then((r) => r.data as DistributorClient[])
  }

  assignDistributorClient(distributorId: string, clientId: string): Promise<void> {
    return fetchRequest
      .post<any>('funds/distributors/' + distributorId + '/clients/' + clientId, {})
      .then(() => undefined)
  }

  unassignDistributorClient(distributorId: string, clientId: string): Promise<void> {
    return fetchRequest
      .remove<any>('funds/distributors/' + distributorId + '/clients/' + clientId)
      .then(() => undefined)
  }

  // Odkupy jednotek
  priceRedemptionQueue(fundKey: StrategyKey, valuationId?: string): Promise<Redemption[]> {
    const query = 'fundKey=' + fundKey + (valuationId ? '&valuationId=' + valuationId : '')
    return fetchRequest
      .post<any>('funds/redemptions/price-queue?' + query, {})
      .then((r) => numify(r.data) as Redemption[])
  }

  listRedemptions(filter?: RedemptionFilter): Promise<Redemption[]> {
    const parts: string[] = []
    if (filter && filter.fundKey) {
      parts.push('fundKey=' + filter.fundKey)
    }
    if (filter && filter.status) {
      parts.push('status=' + filter.status)
    }
    const query = parts.length ? '?' + parts.join('&') : ''
    return fetchRequest
      .get<any>('funds/redemptions' + query)
      .then((r) => numify(r.data) as Redemption[])
  }

  getRedemption(redemptionId: string): Promise<Redemption> {
    return fetchRequest
      .get<any>('funds/redemptions/' + redemptionId)
      .then((r) => numify(r.data) as Redemption)
  }

  createRedemption(request: CreateRedemptionRequest): Promise<Redemption> {
    const body = {
      clientId: request.clientId,
      fundKey: request.fundKey,
      // BUĎ jednotky, NEBO částka — druhé pole zůstane prázdné (BE si to hlídá).
      units: optStr(request.units),
      amount: optStr(request.amount),
      valuationId: request.valuationId,
      accountNumber: request.accountNumber,
      exitFeeRate: optStr(request.exitFeeRate),
      note: request.note,
      feeClass: request.feeClass,
      all: request.all || undefined,
      receivedDate: request.receivedDate,
      valuationDay: request.valuationDay,
    }
    return fetchRequest
      .post<any>('funds/redemptions', body)
      .then((r) => numify(r.data) as Redemption)
  }

  acceptRedemption(redemptionId: string): Promise<Redemption> {
    return fetchRequest
      .put<any>('funds/redemptions/' + redemptionId + '/accept', {})
      .then((r) => numify(r.data) as Redemption)
  }

  confirmRedemption(redemptionId: string, request: ConfirmRedemptionRequest): Promise<Redemption> {
    return fetchRequest
      .put<any>('funds/redemptions/' + redemptionId + '/confirm', request)
      .then((r) => numify(r.data) as Redemption)
  }

  payoutRedemption(redemptionId: string): Promise<Redemption> {
    return fetchRequest
      .put<any>('funds/redemptions/' + redemptionId + '/payout', {})
      .then((r) => numify(r.data) as Redemption)
  }

  cancelRedemption(redemptionId: string): Promise<Redemption> {
    return fetchRequest
      .put<any>('funds/redemptions/' + redemptionId + '/cancel', {})
      .then((r) => numify(r.data) as Redemption)
  }

  redemptionStatement(redemptionId: string): Promise<Blob> {
    // BE vrací přímo PDF (ne base64 DocumentFile), proto blob.
    return fetchRequest
      .get<Blob>('funds/redemptions/' + redemptionId + '/statement', {}, { responseType: 'blob' })
      .then((r) => r.data)
  }

  listFeeRates(): Promise<FeeRate[]> {
    return fetchRequest.get<any>('funds/fee-rates').then((r) => numify(r.data) as FeeRate[])
  }

  updateFeeRate(request: UpdateFeeRateRequest): Promise<FeeRate> {
    const body = {
      fundKey: request.fundKey,
      feeClass: request.feeClass,
      mgmtFeePa: String(request.mgmtFeePa),
      successFeeRate: String(request.successFeeRate),
      // Prázdné = BE nechá stávající sazbu; nula se posílá explicitně jako "0".
      exitFeeRate: optStr(request.exitFeeRate),
      // Bez tohoto pole BE základnu provizí ponechal — uložení z dialogu Sazby ji nikdy nezměnilo.
      commissionBasePa: optStr(request.commissionBasePa),
    }
    return fetchRequest.put<any>('funds/fee-rates', body).then((r) => numify(r.data) as FeeRate)
  }

  listMgmtFeeRuns(): Promise<MgmtFeeRun[]> {
    return fetchRequest.get<any>('funds/mgmt-fee/runs').then((r) => numify(r.data) as MgmtFeeRun[])
  }

  runMgmtFee(request: RunMgmtFeeRequest): Promise<MgmtFeeRun> {
    return fetchRequest
      .post<any>('funds/mgmt-fee/runs', request)
      .then((r) => numify(r.data) as MgmtFeeRun)
  }

  listSuccessFeeRuns(fundKey?: StrategyKey): Promise<SuccessFeeRun[]> {
    const query = fundKey ? '?fundKey=' + fundKey : ''
    return fetchRequest
      .get<any>('funds/success-fee/runs' + query)
      .then((r) => numify(r.data) as SuccessFeeRun[])
  }

  createSuccessFee(request: CreateSuccessFeeRequest): Promise<SuccessFeeRun> {
    const body = {
      fundKey: request.fundKey,
      year: request.year,
      yearEndPrice: optStr(request.yearEndPrice),
    }
    return fetchRequest
      .post<any>('funds/success-fee/runs', body)
      .then((r) => numify(r.data) as SuccessFeeRun)
  }

  confirmSuccessFee(runId: string, request: ConfirmSuccessFeeRequest): Promise<SuccessFeeRun> {
    return fetchRequest
      .post<any>('funds/success-fee/runs/' + runId + '/confirm', request)
      .then((r) => numify(r.data) as SuccessFeeRun)
  }

  deleteSuccessFee(runId: string): Promise<void> {
    return fetchRequest.remove<any>('funds/success-fee/runs/' + runId).then(() => undefined)
  }

  mgmtFeeStatement(runId: string, distributorId: string, format: StatementFormat): Promise<DocumentFile> {
    return fetchRequest
      .get<any>(
        'funds/mgmt-fee/runs/' + runId + '/statement?distributorId=' + encodeURIComponent(distributorId) + '&format=' + format
      )
      .then((r) => r.data as DocumentFile)
  }

  distributorStatement(distributorId: string, from: string, to: string, format: StatementFormat): Promise<DocumentFile> {
    return fetchRequest
      .get<any>(
        'funds/distributors/' + encodeURIComponent(distributorId) + '/statement?from=' + from + '&to=' + to + '&format=' + format
      )
      .then((r) => r.data as DocumentFile)
  }

  successFeeYearStatement(
    year: number,
    target: { distributorId?: string; clientId?: string },
    format: StatementFormat
  ): Promise<DocumentFile> {
    const who = target.distributorId
      ? 'distributorId=' + encodeURIComponent(target.distributorId)
      : 'clientId=' + encodeURIComponent(target.clientId ?? '')
    return fetchRequest
      .get<any>('funds/success-fee/statement?year=' + year + '&' + who + '&format=' + format)
      .then((r) => r.data as DocumentFile)
  }

  successFeeStatement(
    runId: string,
    target: { distributorId?: string; clientId?: string },
    format: StatementFormat
  ): Promise<DocumentFile> {
    const who = target.distributorId
      ? 'distributorId=' + encodeURIComponent(target.distributorId)
      : 'clientId=' + encodeURIComponent(target.clientId ?? '')
    return fetchRequest
      .get<any>('funds/success-fee/runs/' + runId + '/statement?' + who + '&format=' + format)
      .then((r) => r.data as DocumentFile)
  }
}

export const opsApiHttp = new OpsApiHttp()
